/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › Admin changes and device custody (docs/CONTRACT-fleet-governance.md sections 7.2, 9.1 and 12.3): every
 * `admin.*` event and the five device custody events, with their counts over the range and a sparkline; an event opens
 * its audit rows.
 */
import { History } from 'lucide-react';
import { trendsApi, trendsExportHref, type GovChangesAnswer, type GovEventRow } from '../../lib/trends';
import { EmptyState, Grid, Panel, Sparkline, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { ExportLink } from '../fleet/parts';
import { SectionError, useClientSort, type SectionProps } from './parts';

function EventTable({ label, rows, loading, empty, onOpen }: { label: string; rows: readonly GovEventRow[]; loading: boolean; empty: string; onOpen: (row: GovEventRow) => void }) {
  const sorted = useClientSort(rows, (row, key) => (key === 'event' ? row.event : row.count), ['event']);
  const columns: Array<Column<GovEventRow>> = [
    { key: 'event', header: 'Event', sortable: true, cell: (row) => <Tag>{row.event}</Tag> },
    { key: 'count', header: 'Count', numeric: true, sortable: true, cell: (row) => count(row.count) },
    { key: 'spark', header: 'Per day', hideOnPhone: true, cell: (row) => <Sparkline values={row.spark} label={`${row.event} per day`} /> },
  ];
  return (
    <div className="ui-section">
      <h3 className="ui-section-title">{label}</h3>
      <div className="ui-table-box">
        <Table
          label={label}
          columns={columns}
          rows={sorted.rows}
          rowKey={(row) => row.event}
          loading={loading}
          skeletonRows={3}
          stickyHeader={false}
          onRowClick={onOpen}
          sort={sorted.sort}
          empty={<EmptyState compact icon={History} title={empty} />}
        />
      </div>
    </div>
  );
}

export function TrendsChanges({ query, filter, onShowRows }: SectionProps) {
  const { load, reload, reloading } = useLoad(() => trendsApi.changes(query), `changes|${JSON.stringify(query)}`);
  const answer: GovChangesAnswer | null = load.state === 'ready' ? load.data : null;
  const open = (row: GovEventRow) => {
    if (!answer) return;
    onShowRows({ title: row.event, choices: [{ label: row.event, event: row.event }], app: filter.app || null, range: { from: answer.range.from, to: answer.range.to } });
  };
  return (
    <Panel title="Admin changes and device custody" description="Every admin change and device custody event in the range; an event opens its audit rows" actions={<ExportLink href={trendsExportHref('changes', filter)} />}>
      {load.state === 'failed' ? <SectionError title="The changes could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : (
        <Grid cols={2}>
          <EventTable label="Admin changes" rows={answer?.admin ?? []} loading={load.state === 'loading'} empty="No admin changes in this range" onOpen={open} />
          <EventTable label="Device custody" rows={answer?.custody ?? []} loading={load.state === 'loading'} empty="No device custody events in this range" onOpen={open} />
        </Grid>
      )}
    </Panel>
  );
}
